import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import styles from './card-user-info.module.scss';
import clsx from 'clsx';
import { Avatar, BottomSheet, Button, Container, Tag, Typography } from '@/ui-kit';
import { Profile } from '@/types/profile';
import { differenceInYearsResult } from '@/utils/difference-in-years-result';
import { LocationIcon, MagicIcon, OfflineStatus, OnlineStatus, PremiumIcon, VerifiedAccountIcon } from '@/assets/icons';
import { ButtonsLikeAndDislike } from '@/components/buttons-like-and-dislike';
import { dataPurposes } from '@/constans/purposes';
import { PremiumBottomsheet } from '@/components/premium-bottomsheet';
import { getProfileNameAndAge } from '@/utils/msgs';
import { useTranslation } from 'react-i18next';

type Dir = 'left' | 'right';

interface Props {
    currentSwipeSlide: Profile;
    isVisibleDetails: boolean;
    detailsRef: React.Ref<HTMLDivElement>;
    onSwipeLeft: (val: Dir) => void;
    onSwipeRight: (val: Dir) => void;
}

export const CardUserInfo = forwardRef<{ getContentHeight: () => void }, Props>(({
    currentSwipeSlide,
    isVisibleDetails,
    detailsRef,
    onSwipeLeft,
    onSwipeRight,
}, ref) => {
    const currentPurpose = dataPurposes.find((purpose) => purpose.value === currentSwipeSlide?.purpose);

    const getIconComponent = (icon: React.FC<React.SVGProps<SVGSVGElement>>): React.ReactNode => {
        const IconComponent = icon;

        return <IconComponent />;
    };

    useEffect(() => {
        console.log(currentSwipeSlide);
    }, [currentSwipeSlide]);

    const [isVerificationModalOpen, setIsVerificationModalOpen] =
        useState(false);

    const [isPremiumModalOpen, setIsPremiumModalOpen] =
        useState(false);

    const contentRef = useRef<{ getContentHeight: () => number } | null>(null)

    // Expose custom methods to parent via ref
    useImperativeHandle(ref, () => {
        return {
            getContentHeight: () => contentRef.current?.getContentHeight()
        }
    });

    const { t } = useTranslation();

    return (
        <div className={clsx(styles.descriptionWrapper, isVisibleDetails && styles.visibleDetails)}>
            <Container ref={contentRef} className={clsx(styles.infoWrapper, isVisibleDetails && styles.visibleDetails)}>
                <div ref={detailsRef} />
                <div className={styles.userInfo__header}>
                    <Typography
                        typeColor="black"
                        fontWeight="medium"
                        variant="span"
                        className={clsx(styles.city)}>
                        {currentSwipeSlide?.is_online ? (
                            <>
                                <OnlineStatus /> {t('online')}
                            </>
                        ) : (
                            <>
                                <OfflineStatus /> {t('offline')}
                            </>
                        )}
                    </Typography>

                    {currentSwipeSlide?.regions ? (
                        currentSwipeSlide?.regions.map((region) => (
                            <Typography
                                typeColor="black"
                                fontWeight="medium"
                                variant="span"
                                className={clsx(styles.city)}>
                                <LocationIcon /> {region?.name}
                            </Typography>
                        ))
                    ) : (
                        <Typography
                            typeColor="black"
                            fontWeight="medium"
                            variant="span"
                            className={clsx(styles.city)}>
                            {t('empty_regions')}
                        </Typography>
                    )}
                </div>
                {currentSwipeSlide?.public_name && (
                    <div className={styles.nameAndVerificationStatusInfo}>
                        <Typography
                            typeColor="black"
                            fontWeight="medium"
                            variant="h3"
                            className={styles.cardInfoText}>
                            {getProfileNameAndAge(currentSwipeSlide)}
                        </Typography>
                        {
                            currentSwipeSlide
                                ?.verification_status
                                === 'verified' &&
                            <VerifiedAccountIcon
                                width={24}
                                height={24}
                                className={styles.verificationStatusInfo}
                                onClick={() => setIsVerificationModalOpen(true)} />
                        }
                        {
                            currentSwipeSlide.is_paid &&
                            <PremiumIcon
                                width={24}
                                height={24}
                                onClick={() => setIsPremiumModalOpen(true)} />
                        }
                    </div>
                )}

                {currentSwipeSlide?.about_me && (
                    <>
                        <div className={styles.label}>
                            <Typography
                                variant="label"
                                fontWeight="normal"
                                fontFamily="SFProText">
                                {t('about')}
                            </Typography>
                        </div>

                        <Typography className={styles.text} variant="p">
                            {currentSwipeSlide?.about_me}
                        </Typography>
                    </>
                )}

                {currentPurpose && (
                    <Tag className={clsx(styles.tagInfo, styles.isIcon)} icon={getIconComponent(currentPurpose.icon)}>
                        <div>
                            <Typography className={styles.tagInfo__title} variant="p">
                                {t('purpose_label')}
                            </Typography>
                            <Typography
                                className={styles.tagInfo__text}
                                variant="p"
                                fontWeight="bold">
                                {t(currentPurpose.text)}
                            </Typography>
                        </div>
                    </Tag>
                )}

                {currentSwipeSlide?.travel_plans && (
                    <>
                        <div className={styles.label}>
                            <Typography
                                variant="label"
                                fontWeight="normal"
                                fontFamily="SFProText">
                                {t('travel_plans_label')}
                            </Typography>
                        </div>

                        <Typography className={styles.text} variant="p">
                            {currentSwipeSlide?.travel_plans}
                        </Typography>
                    </>
                )}

                <ButtonsLikeAndDislike
                    onSwipeLeft={() => onSwipeLeft('left')}
                    onSwipeRight={() => onSwipeRight('right')}
                    visible={true}
                    customClass={styles.actionButtons} />
            </Container>
            <BottomSheet
                overlayClassName={styles.bottomsheetOverlay}
                isOpen={isVerificationModalOpen}
                isDeskorTop={false}
                onClose={() => setIsVerificationModalOpen(false)}>
                <div className={styles.verificationModalContent}>
                    <div className={styles.verificationModalContentIcon}>
                        <Avatar
                            src={currentSwipeSlide.photos.length ? currentSwipeSlide.photos[0].main_url : null}
                            size={96} />
                        <VerifiedAccountIcon
                            className={styles.verificationModalContentIconVerification}
                            width={28}
                            height={28} />
                    </div>
                    <div className={styles.verificationModalContentHeader}>
                        <Typography
                            className={styles.verificationModalContentHeaderTitle}
                            variant={'h5'}
                            fontWeight={'bold'}>
                            {t('user_pass_verification', { name: currentSwipeSlide.public_name, context: currentSwipeSlide.gender })}
                        </Typography>
                        <Typography
                            className={styles.verificationModalContentHeaderDesc}>
                            {t('verification_desc')}
                        </Typography>
                    </div>
                    <div className={styles.verificationModalContentFootnote}>
                        <Button
                            typeColor="orange"
                            className={styles.verificationModalContentFootnoteBtn}
                            onClick={() => setIsVerificationModalOpen(false)}>
                            {t('ok_tertiary')}
                        </Button>
                    </div>
                </div>
            </BottomSheet>

            <PremiumBottomsheet
                photo={currentSwipeSlide.photos.length ? currentSwipeSlide.photos[0].main_url : null}
                name={currentSwipeSlide.public_name}
                isPremiumModalOpen={isPremiumModalOpen}
                setIsPremiumModalOpen={setIsPremiumModalOpen} />
        </div>
    );
});
